import { EuiEmptyPrompt } from "@elastic/eui";
import {
    Button,
    DateRangePicker,
    DateRangePickerProps,
    Icon,
    Select,
    SpaceBetween,
    Tabs,
    TextContent
} from "@amzn/awsui-components-react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../utils/appContext";
import { useToastNotifications } from "../../../hooks/useToastList";
import { periodOptions as periodDropdownOptions } from "../Collection/Dashboards/utils";
import moment from "moment/moment";
import {
    DAY_1,
    DAYS_15,
    DAYS_455,
    DAYS_63,
    HOUR_1,
    HOURS_3,
    MINUTE_1,
    MINUTE_15,
    MINUTE_5,
    SEC_1
} from "../Collection/Dashboards/constants";
import { i18nStrings, relativeOptions, timezoneOptions } from "../../commons/constants";
import { ExternalWidget } from "../../Components/Cloudwatch/model";
import { ExternalDashboard } from "../../Components/Cloudwatch/ExternalDashboard";
import { Container, Header } from "@amzn/awsui-components-react/polaris";
import { getCgOcuMetrics } from "./utils";

const LoadGraphs = (props: any) => (
    <EuiEmptyPrompt
        title={<h4>Load cloud watch graphs ?</h4>}
        actions={[
            <Button variant="normal" onClick={props.onClick}>
                Display graphs
            </Button>,
        ]}
    />
);

const CollectionGroupDashboards = (props) => {
    const appContext = useContext(AppContext);
    const toastNotifications = useToastNotifications();

    const [loadGraphs, setLoadGraphs] = useState(false);
    const [, setShowFullScreen] = useState<boolean>(false);

    const [date, setDate] = useState<any>({
        amount: 1,
        unit: 'week',
        type: 'relative',
    });

    const [periodOptions, setPeriodOptions] = useState(periodDropdownOptions);

    const [selectedPeriod, setSelectedPeriod] = useState({
        label: '1h',
        value: '3600',
        text: 3600,
    });

    // Valid parsed date from moment
    const [metricRange, setMetricRange] = useState<any>({
        startTime: props.shared
            ? moment(props.shared?.startTime).unix()
            : moment().subtract(1, 'weeks').unix(),
        endTime: props.shared
            ? moment(props.shared?.endTime).unix()
            : moment().unix(),
        period: selectedPeriod.text,
    });

    // Defaults to browser time and not to UTC
    const [utcOffset, setUtcOffset] = useState<number>(0);

    // const [timezoneValue, setTimezoneValue] = useState('Browser Timezone');
    const [selectedOption, setSelectedOption] = React.useState({
        inputDisplay: 'Browser Timezone',
        label: 'Browser Timezone',
        value: 'Browser Timezone',
        text: 0,
    });

    const handleDateChange = useCallback(
        ({ detail }) => {
            var startMoment, endMoment;

            if ('absolute' === detail.value.type) {
                startMoment = moment(detail.value.startDate);
                endMoment = moment(detail.value.endDate);
            } else if ('relative' === detail.value.type) {
                startMoment = moment().subtract(detail.value.amount, detail.value.unit);
                endMoment = moment();
            }

            if (
                !startMoment ||
                !startMoment.isValid() ||
                startMoment.isBefore(moment().subtract(14, 'months'))
            ) {
                toastNotifications.addDanger(
                    'Invalid start date, it can not be older than 14 months'
                );
                return;
            }

            if (!endMoment || !endMoment.isValid() || endMoment.isAfter(moment())) {
                toastNotifications.addDanger(
                    'Invalid end date, it can not be future date'
                );
                return;
            }

            var duration = moment.duration(endMoment.diff(startMoment)).asSeconds();
            var newPeriodOptions = periodDropdownOptions.map((a) => {
                return { ...a };
            });

            let disableOption = (optionToBeDisabled) => {
                // eslint-disable-next-line
                newPeriodOptions.find((option, i) => {
                    if (option.text === optionToBeDisabled) {
                        newPeriodOptions[i]['disabled'] = true;
                        return true;
                    }
                });
            };

            let setPeriod = (periodToBeSet) => {
                setSelectedPeriod(
                    periodOptions.find((option) => option.text === periodToBeSet)
                );
            };

            // metric retention conditions to disable period options
            if (duration <= HOURS_3) {
                // setPeriod(MINUTE_1);
            }
            if (duration > HOURS_3 && duration <= DAYS_15) {
                disableOption(SEC_1);
                // setPeriod(MINUTE_1);
            }
            if (duration > DAYS_15 && duration <= DAYS_63) {
                disableOption(SEC_1);
                disableOption(MINUTE_1);
                // setPeriod(MINUTE_5);
            }
            if (duration > DAYS_63 && duration <= DAYS_455) {
                disableOption(SEC_1);
                disableOption(MINUTE_1);
                disableOption(MINUTE_5);
                // setPeriod(HOUR_1);
            }
            if (duration > DAYS_455) {
                disableOption(SEC_1);
                disableOption(MINUTE_1);
                disableOption(MINUTE_5);
                disableOption(MINUTE_15);
                disableOption(HOUR_1);
                // setPeriod(DAY_1);
            }

            // disabling period options greater than the selected duration
            if (duration < DAY_1) {
                disableOption(DAY_1);
            }
            if (duration < HOUR_1) {
                disableOption(HOUR_1);
            }
            if (duration < MINUTE_15) {
                disableOption(MINUTE_15);
            }
            if (duration < MINUTE_5) {
                disableOption(MINUTE_5);
            }
            if (duration < MINUTE_1) {
                disableOption(MINUTE_1);
            }

            // setting period for default relative date options
            if ('relative' === detail.value.type) {
                let val = detail.value.amount + ' ' + detail.value.unit;
                if (val === '6 hour' || val === '1 day') {
                    setPeriod(MINUTE_1);
                }
                if (val === '3 day' || val === '1 week') {
                    setPeriod(MINUTE_5);
                }
                if (val === '3 week') {
                    setPeriod(MINUTE_15);
                }
            }

            setPeriodOptions(newPeriodOptions);
            setDate(detail.value);
            setMetricRange({
                startTime: startMoment.unix(),
                endTime: endMoment.unix(),
                period: selectedPeriod.text,
            });
        },
        [selectedPeriod, periodOptions, toastNotifications]
    );

    const isValidRange: DateRangePickerProps.ValidationFunction = (e) => {
        const t = (start, end) => {
            const a = moment(end).isBefore(moment(start));
            return a;
        };
        if ('absolute' === e.type) {
            const [a] = e.startDate.split('T'),
                [n] = e.endDate.split('T');
            if (!a || !n)
                return {
                    valid: !1,
                    errorMessage:
                        'The selected date range is incomplete. Select a start and end date for the date range.',
                };
            if (t(e.startDate, e.endDate))
                return {
                    valid: !1,
                    errorMessage: 'The end date must be greater than the start date.',
                };
        } else if ('relative' === e.type) {
            if (isNaN(e.amount))
                return {
                    valid: !1,
                    errorMessage:
                        'The selected date range is incomplete. Specify a duration for the date range.',
                };
        }
        return { valid: !0 };
    };

    const handleTimezoneChange = (selectedOption) => {
        setUtcOffset(selectedOption.text);
        setSelectedOption(selectedOption);
    };

    const handlePeriodChange = (selectedPeriodOption) => {
        setSelectedPeriod(selectedPeriodOption);
    };

    const getValidPeriod = (startTime: number, endTime: number, requestedPeriod: number) => {
        const timeRangeSeconds = endTime - startTime;
        const maxDatapoints = 1440;
        const minRequiredPeriod = Math.ceil(timeRangeSeconds / maxDatapoints);

        // Return the larger of requested period or minimum required period
        return Math.max(requestedPeriod, minRequiredPeriod);
    };

    useEffect(() => {
        const startTime = metricRange.startTime;
        const endTime = metricRange.endTime;
        const validPeriod = getValidPeriod(startTime, endTime, selectedPeriod.text);

        // Only update if period changed
        if (validPeriod !== metricRange.period) {
            const newMetric = { startTime, endTime, period: validPeriod };
            setMetricRange(newMetric);
        }
    }, [selectedPeriod.text, metricRange.startTime, metricRange.endTime, metricRange.period]);


    const tabs = [
        {
            id: 'ocu',
            name: 'OCU History',
            label: 'OCU History',
            content: loadGraphs && props.collectionGroupDetails ? (
                <div>
                    <ExternalDashboard
                        widgets={[
                            ...(getCgOcuMetrics(
                                props.awsAccountId,
                                props.collectionGroupDetails.id,
                                props.collectionGroupDetails.name,
                                appContext.region.publicRegion,
                                false
                            ) as ExternalWidget[]),
                        ]}
                        metricRange={metricRange}
                        utcOffset={utcOffset}
                        ocuLimits={props.ocuLimits} // add min/max annotations
                    />
                </div>
            ) : (
                <LoadGraphs onClick={() => setLoadGraphs(true)} />
            ),
            disabled: false
        }
    ];

    return (
        <Container
            disableContentPaddings
            header={
                <Header
                    variant="h2"
                    actions={
                        <SpaceBetween direction="horizontal" size="s">
                            <DateRangePicker
                                value={date}
                                i18nStrings={i18nStrings}
                                placeholder="Filter by a date and time range"
                                onChange={handleDateChange}
                                relativeOptions={relativeOptions}
                                isValidRange={isValidRange}
                                showClearButton={false}
                            />
                            <TextContent>
                                <strong>Period :</strong>
                            </TextContent>
                            <Select
                                selectedOption={selectedPeriod}
                                onChange={({ detail }) =>
                                    handlePeriodChange(detail.selectedOption)
                                }
                                options={periodOptions}
                                selectedAriaLabel="Selected"
                            />
                            <TextContent>
                                <strong>Timezone :</strong>
                            </TextContent>
                            <Select
                                selectedOption={selectedOption}
                                onChange={({ detail }) =>
                                    handleTimezoneChange(detail.selectedOption)
                                }
                                options={timezoneOptions}
                                selectedAriaLabel="Selected"
                            />
                            <Button
                                variant="link"
                                onClick={() =>
                                    setShowFullScreen(
                                        (latestFullScreen: boolean) => !latestFullScreen
                                    )
                                }
                            >
                                <Icon name="view-full" size="normal" variant="normal" />
                            </Button>
                        </SpaceBetween>
                    }
                >
                    Utilization
                </Header>
            }
        >
            <Tabs tabs={tabs} />
        </Container>
    );
}

export { CollectionGroupDashboards };
